import { Button } from "@pankod/refine-antd";
import {
  useApiUrl,
  useCustomMutation,
  useNotification,
} from "@pankod/refine-core";
import { createElement, useRef, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";

import { FirstAccessWhatsappConfirmationModal } from "./first-access-whatsapp-confirmation-modal";
import {
  FIRST_ACCESS_WHATSAPP_RESEND_LABEL,
  FIRST_ACCESS_WHATSAPP_RESEND_UNAVAILABLE_LABEL,
  FirstAccessWhatsappResendResponse,
  getFirstAccessWhatsappResendErrorMessage,
  getFirstAccessWhatsappResendSuccessMessage,
  isFirstAccessWhatsappResendEligible,
} from "../utils/first-access-whatsapp-resend";

type FirstAccessWhatsappResendActionProps = {
  budgetId?: string;
  status?: string | number | null;
  canExecute: boolean;
  onSuccess?: () => void;
};

export const FirstAccessWhatsappResendAction: React.FC<
  FirstAccessWhatsappResendActionProps
> = ({ budgetId, status, canExecute, onSuccess }) => {
  const apiUrl = useApiUrl();
  const notification = useNotification();
  const [visible, setVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const { mutate, isLoading } = useCustomMutation();

  if (!canExecute) {
    return null;
  }

  const eligible =
    Boolean(budgetId) && isFirstAccessWhatsappResendEligible(status);
  const pending = isLoading || isSubmitting;
  const disabled = !eligible || pending;
  const buttonClassName = eligible
    ? "first-access-whatsapp-action first-access-whatsapp-action--enabled"
    : "first-access-whatsapp-action first-access-whatsapp-action--blocked";

  const handleClick = () => {
    if (!eligible || pending) {
      return;
    }

    setVisible(true);
  };

  const handleCancel = () => {
    if (pending) {
      return;
    }

    setVisible(false);
  };

  const handleConfirm = () => {
    if (!budgetId || submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    mutate(
      {
        url: `${apiUrl}/v1/budget/${budgetId}/send-first-access-message`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (response) => {
          submittingRef.current = false;
          setIsSubmitting(false);
          setVisible(false);
          notification.open?.({
            type: "success",
            message: getFirstAccessWhatsappResendSuccessMessage(
              response?.data as FirstAccessWhatsappResendResponse,
            ),
          });
          onSuccess?.();
        },
        onError: (error) => {
          submittingRef.current = false;
          setIsSubmitting(false);
          setVisible(false);
          notification.open?.({
            type: "error",
            message: getFirstAccessWhatsappResendErrorMessage(error),
          });
        },
      },
    );
  };

  return (
    <>
      <Button
        aria-label={FIRST_ACCESS_WHATSAPP_RESEND_LABEL}
        className={buttonClassName}
        disabled={disabled}
        icon={
          !pending
            ? createElement(FaWhatsapp as React.ElementType, {
                "aria-hidden": true,
              })
            : undefined
        }
        loading={pending}
        onClick={handleClick}
        size="small"
        title={
          eligible
            ? FIRST_ACCESS_WHATSAPP_RESEND_LABEL
            : FIRST_ACCESS_WHATSAPP_RESEND_UNAVAILABLE_LABEL
        }
      />
      <FirstAccessWhatsappConfirmationModal
        visible={visible}
        loading={pending}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
};
